import { useEffect } from "react";

export default function IntonationHelpModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const intonationImageSrc =
    `${process.env.REACT_APP_USER_API_URL}/uploads/screens/muchwarmerthere.png`;
  const voicingImageSrc =
    `${process.env.REACT_APP_USER_API_URL}/uploads/screens/gutten_skriver.png`;

  return (
    <div className="fixed inset-0 z-[250]">
      <button
        type="button"
        aria-label="Close help"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />

      <div className="absolute inset-0 p-0 sm:p-4">
        <div
          className="relative flex h-full w-full flex-col overflow-hidden bg-white sm:rounded-2xl sm:border sm:border-gray-200 sm:shadow-2xl"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="sticky top-0 z-20 border-b border-gray-200 bg-white">
            <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6">
              <div className="min-w-0">
                <div className="truncate text-base font-semibold text-gray-900 sm:text-lg">
                  How to read the graph
                </div>
                <div className="truncate text-sm text-gray-600">
                  Pitch movement, sound intensity, and voiced/unvoiced sounds
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50"
              >
                <span className="text-base leading-none">✕</span>
                <span className="hidden sm:inline">Close</span>
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="space-y-7 px-4 py-5 sm:px-6">
              <section className="space-y-4">
                <div>
                  <div className="text-base font-semibold text-gray-900">
                    1. Follow the overall pitch movement
                  </div>
                  <p className="mt-1 max-w-4xl text-sm leading-relaxed text-gray-600">
                    In “Wouldn’t it be much warmer there?”, the blue pitch curve
                    flows across several words instead of restarting at every word
                    boundary. Focus first on the broad rise-and-fall pattern, not
                    every tiny movement.
                  </p>
                </div>

                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                  <img
                    src={intonationImageSrc}
                    alt="Pitch and intensity curves for the question Wouldn't it be much warmer there?"
                    className="block h-auto w-full"
                  />
                </div>

                <div className="grid gap-3 text-sm leading-relaxed text-gray-700 md:grid-cols-3">
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div className="font-semibold text-gray-900">
                      One phrase-level movement
                    </div>
                    <p className="mt-1">
                      “Wouldn’t it be much” forms one connected intonation
                      movement rather than a separate contour for every word.
                    </p>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div className="font-semibold text-gray-900">
                      Movement inside words
                    </div>
                    <p className="mt-1">
                      Pitch can rise or fall inside “warmer” and “there”. Word
                      boundaries do not define the contour.
                    </p>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div className="font-semibold text-gray-900">
                      Rising question tendency
                    </div>
                    <p className="mt-1">
                      The phrase shows an overall rise toward the question ending.
                      Match the broad tendency before fine details.
                    </p>
                  </div>
                </div>
              </section>

              <section className="space-y-4">
                <div>
                  <div className="text-base font-semibold text-gray-900">
                    2. Understand why the blue curve has gaps
                  </div>
                  <p className="mt-1 max-w-4xl text-sm leading-relaxed text-gray-600">
                    Pitch is measurable only while the vocal folds vibrate. The
                    grey intensity curve can continue through an unvoiced sound
                    because sound energy is still present even when pitch is not.
                  </p>
                </div>

                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                  <img
                    src={voicingImageSrc}
                    alt="Pitch and intensity curves for Gutten skriver, showing pitch gaps during tt and sk"
                    className="block h-auto w-full"
                  />
                </div>

                <div className="rounded-2xl border border-blue-200 bg-blue-50/40 p-4 sm:p-5">
                  <div className="font-semibold text-gray-900">
                    Simplified voicing pattern
                  </div>

                  <div className="mt-3 overflow-x-auto">
                    <div className="inline-grid min-w-[560px] grid-cols-5 overflow-hidden rounded-xl border border-gray-200 bg-white text-center">
                      {[
                        ["Gu", "voiced", true],
                        ["tt", "unvoiced", false],
                        ["en", "voiced", true],
                        ["sk", "unvoiced", false],
                        ["river", "voiced", true],
                      ].map(([sound, label, voiced], index) => (
                        <div
                          key={sound}
                          className={index < 4 ? "border-r border-gray-200" : ""}
                        >
                          <div
                            className={[
                              "px-3 py-2 font-semibold",
                              voiced ? "text-blue-700" : "text-slate-600",
                            ].join(" ")}
                          >
                            {sound}
                          </div>
                          <div
                            className={[
                              "border-t border-gray-200 px-3 py-2 text-xs font-medium",
                              voiced
                                ? "bg-blue-50 text-blue-700"
                                : "bg-slate-50 text-slate-600",
                            ].join(" ")}
                          >
                            {label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4 grid gap-3 text-sm leading-relaxed text-gray-700 lg:grid-cols-2">
                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                      <div className="font-semibold text-gray-900">
                        Blue pitch curve
                      </div>
                      <p className="mt-1">
                        The curve is present during voiced parts such as{" "}
                        <span className="font-semibold text-blue-700">Gu</span>,{" "}
                        <span className="font-semibold text-blue-700">en</span>,{" "}
                        and the simplified{" "}
                        <span className="font-semibold text-blue-700">river</span>{" "}
                        portion. It disappears during unvoiced{" "}
                        <span className="font-semibold text-slate-700">tt</span>{" "}
                        and{" "}
                        <span className="font-semibold text-slate-700">sk</span>.
                      </p>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                      <div className="font-semibold text-gray-900">
                        Grey intensity curve
                      </div>
                      <p className="mt-1">
                        Intensity measures sound strength, so it can remain visible
                        through both voiced and unvoiced sounds. A blue gap does
                        not mean that the pitch falls to zero; it means there is
                        no measurable pitch at that moment.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="rounded-2xl border border-gray-200 bg-white p-5">
                <div className="font-semibold text-gray-900">
                  3. More gaps than the narrator?
                </div>
                <p className="mt-1 text-sm leading-relaxed text-gray-700">
                  Some gaps are normal because of unvoiced sounds. Extra gaps in
                  your own curve may indicate brief pauses, a breath reset,
                  unusually long closures on{" "}
                  <span className="font-semibold">p, t, k</span>,
                  or loss of voicing where the narrator remains voiced.
                </p>

                <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm leading-relaxed text-gray-700">
                  Compare the{" "}
                  <span className="font-semibold">location and duration</span>{" "}
                  of your gaps with the narrator’s—not simply the total number of
                  gaps. Try carrying the phrase through more smoothly, then replay
                  the relevant word or phrase.
                </div>

                <p className="mt-3 text-xs text-gray-500">
                  Pitch tracking can also fail briefly in noisy recordings.
                </p>
              </section>

              <div className="rounded-xl border border-blue-100 bg-blue-50/50 px-4 py-3 text-sm text-gray-700">
                <span className="font-semibold text-gray-900">Remember:</span>{" "}
                the curve’s{" "}
                <span className="font-semibold text-blue-700">shape</span>{" "}
                shows intonation, while its{" "}
                <span className="font-semibold">gaps</span>{" "}
                show absence of measurable voicing—not absence of sound.
              </div>

              <div className="text-xs text-gray-500">
                Tip: click words in the visualization to replay them and connect
                what you hear with the graph.
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 z-20 border-t border-gray-200 bg-white">
            <div className="flex items-center justify-end px-4 py-3 sm:px-6">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-95"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}